/**
 * TanStack Query hooks for `advertiser-signup`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	review,
	advertiserSignupQueryKeys,
	type AdvertiserSignupStatus,
	type CreateAdvertiserSignupBody,
	type ListAdvertiserSignupResponse,
	type AdvertiserSignupRow,
	type ReviewAdvertiserSignupBody,
} from "./api";

export function useAdvertiserSignupList(
	status?: AdvertiserSignupStatus | "all",
): UseQueryResult<ListAdvertiserSignupResponse, Error> {
	return useQuery({
		queryKey: advertiserSignupQueryKeys.list(status),
		queryFn: () => list(status),
	});
}

export function useAdvertiserSignupCreate(): UseMutationResult<
	AdvertiserSignupRow,
	Error,
	CreateAdvertiserSignupBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSignupQueryKeys.all });
		},
	});
}

export function useAdvertiserSignupReview(): UseMutationResult<
	AdvertiserSignupRow,
	Error,
	{ id: string; body: ReviewAdvertiserSignupBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => review(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSignupQueryKeys.all });
		},
	});
}

export function useAdvertiserSignupDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSignupQueryKeys.all });
		},
	});
}
